/**
 * Scene Readiness Manager
 * Tracks when all scene components are fully loaded and ready to display
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'

const SceneReadyContext = createContext(null)

export function SceneReadyProvider({ children, onAllReady }) {
  const [readyComponents, setReadyComponents] = useState({})
  const [allComponentsReady, setAllComponentsReady] = useState(false)

  // Register/update a component's ready state
  const setComponentReady = useCallback((componentName, isReady) => {
    setReadyComponents(prev => ({
      ...prev,
      [componentName]: isReady
    }))
  }, [])

  // Check if all registered components are ready
  useEffect(() => {
    const componentNames = Object.keys(readyComponents)
    
    // Need at least one component registered
    if (componentNames.length === 0) {
      setAllComponentsReady(false)
      return
    }

    // Check if all components are ready
    const allReady = componentNames.every(name => readyComponents[name] === true)
    
    if (allReady && !allComponentsReady) {
      setAllComponentsReady(true)
      if (onAllReady) {
        onAllReady()
      }
    }
  }, [readyComponents, allComponentsReady, onAllReady])

  const value = {
    setComponentReady,
    readyComponents,
    allComponentsReady
  }

  return (
    <SceneReadyContext.Provider value={value}>
      {children}
    </SceneReadyContext.Provider>
  )
}

/**
 * Hook for components to report their ready state
 * @param {string} componentName - Unique identifier for the component
 * @param {boolean} isReady - Whether the component is fully loaded and ready
 */
export function useSceneReady(componentName, isReady) {
  const context = useContext(SceneReadyContext)
  
  if (!context) {
    return
  }

  const { setComponentReady } = context
  const isReadyRef = useRef(isReady)
  
  // Only update when ready state actually changes to prevent loops
  useEffect(() => {
    if (isReadyRef.current !== isReady) {
      isReadyRef.current = isReady
      setComponentReady(componentName, isReady)
    }
  }, [componentName, isReady, setComponentReady])
}

export default SceneReadyProvider
